<template>
  <div>
    <vs-button v-if="profile && profile.resultPdfFileUrl" class="mb-4" @click="handleDocumentClick(profile.resultPdfFileUrl)">
      Download Result PDF
    </vs-button>
    <div class="grid gap-4 md:grid-cols-2">
      <vx-card>
        <h3 class="mb-6">Profile Details</h3>
        <vs-row v-for="(item, i) in displayItems" :key="i" class="mb-4">
          <vs-col vs-w="5" class="font-medium">{{ item.label }}:</vs-col>
          <vs-col vs-w="7">
            <span v-if="item.isDate">{{ getKeyValueFromContent(item.key) | moment('DD MMM YYYY, hh:mm A') }}</span>
            <span v-else>{{ getKeyValueFromContent(item.key) || '-' }}</span>
          </vs-col>
        </vs-row>
      </vx-card>

      <vx-card>
        <h3>Tracking Details</h3>
        <div class="timeline-container">
          <ul>
            <li v-for="(item, i) in statusTimeline" :key="i" :class="{'incompleted': item.createdAt == null}">
              <div class="timestamp">
                {{ item.createdAt | moment('DD MMM YYYY, hh:mm A') }}
              </div>
              <div class="font-semibold">{{ item.title }}</div>
              <div>{{ item.description }}</div>

              <vs-button v-if="isStatusEditable && item.status == 'RECEIVED_BY_EOSTRE'" @click="submit" class="mt-2">
                Received By Eostre
              </vs-button>
            </li>
          </ul>
        </div>
      </vx-card>

      <vx-card v-if="canUploadReport">
        <h3 class="mb-4">Upload Report</h3>
        <upload-result-form
          :profile-id='profileId'
          :pdfEn='profile ? profile.resultPdfFileUrlEn || profile.resultPdfFileUrl : null'
          :pdfZh='profile ? profile.resultPdfFileUrlZh : null'
          :xml='profile ? profile.resultRawFileUrl : null'
          @upload-success='fetchData'
        />
      </vx-card>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import UploadResultForm from './UploadResultForm'

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    UploadResultForm,
  },
  data() {
    return {
      status: '',
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },
    profileId() {
      return this.$route.params.id || undefined;
    },
    displayItems() {
      return [
        { label: 'DNA Code', key: 'dnaCode', isDate: false },
        { label: 'Full Name', key: 'fullName', isDate: false },
        { label: 'Date of Birth', key: 'dob', isDate: false },
        { label: 'Gender', key: 'gender', isDate: false },
        { label: 'Phone Number', key: 'phoneNumber', isDate: false },
        { label: 'Email', key: 'email', isDate: false },
        { label: 'IC NO', key: 'icNo', isDate: false },
        { label: 'Lab Sample Id', key: 'labSampleId', isDate: false },
        { label: 'Address Line 1', key: 'addressLine1', isDate: false },
        { label: 'Address Line 2', key: 'addressLine2', isDate: false },
        { label: 'Address Line 3', key: 'addressLine3', isDate: false },
        { label: 'Postcode', key: 'postcode', isDate: false },
        { label: 'State', key: 'state', isDate: false },
        { label: 'City', key: 'city', isDate: false },
        { label: 'Country', key: 'country', isDate: false },
        { label: 'Created At', key: 'createdAt', isDate: true },
      ]
    },
    getKeyValueFromContent() {
      return key => _.get(this.profile, key);
    },
    isStatusEditable() {
      // only user registered
      return this.status == 'USER_REGISTERED';
    },
    canUploadReport() {
      return true;
    },
    statusTimeline() {
      return (this.profile && this.profile.tracking) ? _.sortBy(this.profile.tracking, 'createdAt') : [];
    },
    ...mapState({
      profile: ({ helloDnaProfile }) => helloDnaProfile.helloDnaProfile,
      updateProfileState: ({ helloDnaProfile }) => helloDnaProfile.apiState.updateHelloDnaProfile,
    }),
  },
  methods: {
    ...mapActions({
      getProfile: 'helloDnaProfile/getHelloDnaProfile',
      updateProfile: 'helloDnaProfile/updateHelloDnaProfile',
    }),
    async submit() {
      return this.$vs.dialog({
        color: 'primary',
        title: `Are you sure submit?`,
        text: 'Continue submit',
        acceptText: 'Yes',
        accept: () => {
          const body = {
            ...this.profile,
            status: 'RECEIVED_BY_EOSTRE',
          };

          return this.updateProfile({
            id: this.profileId,
            body,
          }).then(() => {
            this.fetchData();
          });
        },
      });
    },
    async fetchData() {
      await this.getProfile(this.profileId).then(() => {
        this.status = this.profile.status;
      });
    },
    handleDocumentClick(url) {
      window.open(url, '_blank');
    }
  },
  async mounted() {
    await this.fetchData();
  },
  watch: {
    'updateProfileState.success'(value) {
      if (value) {
        this.$vs.notify({
          title: 'Successful update!',
          color: 'success',
          position: 'top-center',
        });
      }
    },
    'updateProfileState.error'(error) {
      if (error) {
        this.$vs.notify({
          title: 'Error!',
          text: error.data.message,
          color: 'danger',
          position: 'top-center',
        });
      }
    },
  },
};
</script>

<style lang="scss">
.timeline-container {
  ul {
    margin: 2.5em 0;
    padding: 0;
    display: inline-block;

    li {
      list-style: none;
      margin: auto;
      min-height: 50px;
      border-left: 1px dashed rgb(158, 158, 158);
      padding: 0 0 50px 30px;
      position: relative;

      &:last-child {
        border-left: 0;
      }

      &::before {
        position: absolute;
        left: -18px;
        top: -5px;
        content: " ";
        border: 8px solid rgba(255, 255, 255, 1);
        border-radius: 500%;
        background: rgba(var(--vs-primary), 1);
        height: 20px;
        width: 20px;
        transition: all 500ms ease-in-out;
      }

      &.incompleted {
        color: #9E9E9E !important;
        &::before{
          background: #9E9E9E;
        }
      }

      .timestamp {
        color: rgba(var(--vs-primary), 1);
        position: relative;
        font-size: 12px;
      }
    }
  }
}
</style>
